<template>
  <section class="gastos-list-view">
    <!-- ════════════ TOPBAR ════════════ -->
    <div class="glv-topbar">
      <div class="glv-topbar__left">
        <span class="glv-topbar__title">Gastos</span>
      </div>
      <div class="glv-topbar__right">
        <!-- Botón Sync SRI (cuando hay credenciales guardadas) -->
        <el-tooltip
          :content="
            sriLastSync
              ? `Último sync: ${formatDate(sriLastSync)}`
              : 'Sincronizar facturas SRI'
          "
          placement="bottom"
        >
          <button
            class="glv-btn-sri-sync"
            :class="{ 'is-syncing': sriSyncing }"
            @click="forceSyncSRI"
            :disabled="sriSyncing"
          >
            <i :class="sriSyncing ? 'el-icon-loading' : 'el-icon-refresh'"></i>
            <span>{{ sriSyncing ? "Sync..." : "Sync SRI" }}</span>
          </button>
        </el-tooltip>

        <!-- Importar desde SRI (manual) -->
        <button
          class="glv-btn-sri"
          @click="sriModalVisible = true"
          title="Importar facturas del SRI manualmente"
        >
          <i class="el-icon-download"></i>
          <span>SRI</span>
        </button>
        <button class="glv-btn-primary-action" @click="openForm">
          <i class="el-icon-plus"></i>
          <span>Añadir</span>
        </button>
        <button
          class="glv-icon-btn"
          :class="{ 'has-filters': activeFilterCount > 0 }"
          @click="filterOpen = !filterOpen"
          title="Filtros"
        >
          <i class="el-icon-s-operation"></i>
          <span v-if="activeFilterCount > 0" class="glv-badge">{{
            activeFilterCount
          }}</span>
        </button>
        <el-tooltip content="Descargar Excel" placement="top">
          <button class="dir-view-toggle export-btn-excel" @click="exportExcel">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
              <line x1="10" y1="9" x2="14" y2="9" />
            </svg>
            <span class="export-btn-label">XLS</span>
          </button>
        </el-tooltip>
        <el-tooltip content="Descargar PDF" placement="top">
          <button class="dir-view-toggle glv-btn-pdf" @click="exportPdf">
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="12" x2="12" y2="18" />
              <polyline points="9 15 12 18 15 15" />
            </svg>
            <span class="export-btn-label">PDF</span>
          </button>
        </el-tooltip>
      </div>
    </div>

    <!-- ════════════ BARRA DE PERÍODO ════════════ -->
    <div class="glv-period-bar">
      <!-- Navegación -->
      <div class="glv-period-nav">
        <button
          class="glv-period-nav__btn"
          @click="prevPeriod"
          title="Período anterior"
        >
          <i class="el-icon-arrow-left"></i>
        </button>
        <button class="glv-period-nav__label" @click="goToToday">
          {{ periodLabel }}
        </button>
        <button
          class="glv-period-nav__btn"
          @click="nextPeriod"
          title="Período siguiente"
        >
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>

      <!-- Toggle Mes / Semana -->
      <div class="glv-period-toggle">
        <button
          class="glv-period-toggle__btn"
          :class="{ active: periodMode === 'month' }"
          @click="setPeriodMode('month')"
        >
          Mes
        </button>
        <button
          class="glv-period-toggle__btn"
          :class="{ active: periodMode === 'week' }"
          @click="setPeriodMode('week')"
        >
          Semana
        </button>
      </div>
    </div>

    <!-- ════════════ SUMMARY BAR ════════════ -->
    <div class="glv-summary" v-if="!loading && expenses.length > 0">
      <div class="glv-summary__item">
        <span class="glv-summary__label">Total gastos</span>
        <span class="glv-summary__value"
          >${{ formatAmount(totals.grand_total) }}</span
        >
      </div>
      <div class="glv-summary__sep"></div>
      <div class="glv-summary__item">
        <span class="glv-summary__label">Costos directos</span>
        <span class="glv-summary__value"
          >${{ formatAmount(totals.direct_total) }}</span
        >
      </div>
      <div class="glv-summary__sep"></div>
      <div class="glv-summary__item">
        <span class="glv-summary__label">Gastos operativos</span>
        <span class="glv-summary__value"
          >${{ formatAmount(totals.overhead_total) }}</span
        >
      </div>
      <div class="glv-summary__sep"></div>
      <div class="glv-summary__item">
        <span class="glv-summary__label">Total registros</span>
        <span class="glv-summary__value">{{ expenses.length }}</span>
      </div>
      <template v-if="totals.sri_count > 0">
        <div class="glv-summary__sep"></div>
        <div class="glv-summary__item">
          <span class="glv-summary__label">Importadas SRI</span>
          <span class="glv-summary__value glv-summary__value--teal">{{
            totals.sri_count
          }}</span>
        </div>
      </template>
    </div>

    <!-- ════════════ CHIPS DE FILTROS ACTIVOS ════════════ -->
    <div class="glv-chips" v-if="activeFilterCount > 0">
      <span v-if="filters.vendor" class="glv-chip">
        Proveedor: {{ filters.vendor }}
        <button @click="removeFilter('vendor')">
          <i class="el-icon-close"></i>
        </button>
      </span>
      <span v-if="filters.month" class="glv-chip">
        Mes: {{ filters.month }}
        <button @click="removeFilter('month')">
          <i class="el-icon-close"></i>
        </button>
      </span>
      <span v-if="filters.expense_type" class="glv-chip">
        Tipo:
        {{
          filters.expense_type === "direct"
            ? "Costo directo"
            : "Gasto operativo"
        }}
        <button @click="removeFilter('expense_type')">
          <i class="el-icon-close"></i>
        </button>
      </span>
      <span v-for="cat in filters.categories" :key="cat" class="glv-chip">
        {{ categoryLabel(cat) }}
        <button @click="removeCategoryFilter(cat)">
          <i class="el-icon-close"></i>
        </button>
      </span>
      <span
        v-if="filters.has_project && filters.has_project !== 'all'"
        class="glv-chip"
      >
        Proyecto:
        {{ filters.has_project === "yes" ? "Con proyecto" : "Sin proyecto" }}
        <button @click="removeFilter('has_project')">
          <i class="el-icon-close"></i>
        </button>
      </span>
      <span v-if="filters.origin" class="glv-chip glv-chip--sri">
        {{
          filters.origin === "sri" ? "Importadas del SRI" : "Ingresadas manual"
        }}
        <button @click="removeFilter('origin')">
          <i class="el-icon-close"></i>
        </button>
      </span>
    </div>

    <!-- ════════════ CONTENIDO PRINCIPAL ════════════ -->
    <div class="glv-body" :class="{ 'filter-open': filterOpen }">
      <!-- ── LISTADO ── -->
      <div class="glv-list">
        <!-- Loading skeleton -->
        <template v-if="loading">
          <div v-for="i in 4" :key="i" class="glv-skeleton">
            <div class="glv-skeleton__avatar"></div>
            <div class="glv-skeleton__lines">
              <div class="glv-skeleton__line glv-skeleton__line--lg"></div>
              <div class="glv-skeleton__line glv-skeleton__line--sm"></div>
            </div>
            <div class="glv-skeleton__amount"></div>
          </div>
        </template>

        <!-- Empty state -->
        <div v-else-if="expenses.length === 0" class="glv-empty">
          <i class="el-icon-document-remove glv-empty__icon"></i>
          <p class="glv-empty__text">Sin gastos registrados</p>
          <button class="glv-btn-primary" @click="openForm">
            + Agregar gasto
          </button>
        </div>

        <!-- Agrupados por mes -->
        <template v-else>
          <div
            v-for="(group, monthKey) in groupedByMonth"
            :key="monthKey"
            class="glv-month-group"
          >
            <div class="glv-month-header">
              <span class="glv-month-header__label">{{ monthKey }}</span>
              <span class="glv-month-header__total"
                >${{ formatAmount(group.total) }}</span
              >
            </div>

            <div
              v-for="expense in group.items"
              :key="expense.id"
              class="glv-card"
              :class="{ 'glv-card--no-category': !expense.category }"
            >
              <!-- Avatar -->
              <div class="glv-card__avatar" :class="avatarClass(expense)">
                <span>{{
                  initials(expense.vendor || expense.description)
                }}</span>
              </div>

              <!-- Centro -->
              <div class="glv-card__body">
                <div class="glv-card__name">
                  {{ expense.vendor || "Sin proveedor" }}
                </div>
                <div class="glv-card__badges">
                  <!-- Badge origen SRI -->
                  <span
                    v-if="expense.origin === 'sri'"
                    class="glv-badge-origin glv-badge-origin--sri"
                  >
                    SRI
                  </span>
                  <span
                    class="glv-badge-cat"
                    :class="'glv-badge-cat--' + expense.category"
                  >
                    {{ categoryLabel(expense.category) }}
                  </span>
                  <span
                    class="glv-badge-type"
                    :class="'glv-badge-type--' + expense.expense_type"
                  >
                    {{
                      expense.expense_type === "direct"
                        ? "Directo"
                        : "Operativo"
                    }}
                  </span>
                  <span v-if="expense.project" class="glv-badge-project">
                    <i class="el-icon-folder"></i> {{ expense.project.name }}
                  </span>
                </div>
                <div class="glv-card__desc" v-if="expense.description">
                  {{ expense.description }}
                </div>
                <div v-if="!expense.category" class="glv-card__no-cat">
                  Sin categoría
                </div>
              </div>

              <!-- Derecha -->
              <div class="glv-card__right">
                <span class="glv-card__amount"
                  >${{ formatAmount(expense.amount) }}</span
                >
                <span class="glv-card__date">{{
                  formatDate(expense.expense_date)
                }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <!-- /.glv-body -->

    <!-- ── OVERLAY ── -->
    <transition name="fade-overlay">
      <div
        v-if="filterOpen"
        class="glv-filter-overlay"
        @click="filterOpen = false"
      ></div>
    </transition>

    <!-- ── PANEL DE FILTROS (position: fixed, fuera del overflow del layout) ── -->
    <transition name="slide-filter">
      <div v-if="filterOpen" class="glv-filter-panel">
        <div class="glv-filter-panel__header">
          <span>Filtros</span>
          <button class="glv-icon-btn" @click="filterOpen = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="glv-filter-panel__body">
          <!-- Proveedor -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">Proveedor</label>
            <div class="glv-filter-input-wrap">
              <input
                v-model="draft.vendor"
                type="text"
                class="glv-filter-input"
                placeholder="Buscar proveedor"
              />
              <i class="el-icon-search glv-filter-input__icon"></i>
            </div>
          </div>

          <!-- Mes -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">
              Mes / período
              <i class="el-icon-info glv-filter-label__info"></i>
            </label>
            <el-date-picker
              v-model="draft.month"
              type="month"
              placeholder="Seleccionar mes"
              format="MMMM yyyy"
              value-format="yyyy-MM"
              size="small"
              style="width: 100%"
              :clearable="true"
            />
          </div>

          <!-- Ordenar por -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">Ordenar por</label>
            <div class="glv-order-grid">
              <button
                class="glv-order-btn"
                :class="{ active: draft.order === 'amount_desc' }"
                @click="draft.order = 'amount_desc'"
              >
                Mayor valor
              </button>
              <button
                class="glv-order-btn"
                :class="{ active: draft.order === 'amount_asc' }"
                @click="draft.order = 'amount_asc'"
              >
                Menor valor
              </button>
            </div>
          </div>

          <!-- Tipo -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">Tipo de gasto</label>
            <div class="glv-radio-group">
              <label
                class="glv-radio"
                v-for="opt in typeOptions"
                :key="opt.value"
              >
                <input
                  type="radio"
                  v-model="draft.expense_type"
                  :value="opt.value"
                />
                {{ opt.label }}
              </label>
            </div>
          </div>

          <!-- Categoría -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">Categoría</label>
            <div class="glv-check-group">
              <label
                class="glv-check"
                v-for="cat in categoryOptions"
                :key="cat.value"
              >
                <input
                  type="checkbox"
                  v-model="draft.categories"
                  :value="cat.value"
                />
                {{ cat.label }}
              </label>
            </div>
          </div>

          <!-- Proyecto asignado -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">Proyecto asignado</label>
            <div class="glv-radio-group">
              <label
                class="glv-radio"
                v-for="opt in projectOptions"
                :key="opt.value"
              >
                <input
                  type="radio"
                  v-model="draft.has_project"
                  :value="opt.value"
                />
                {{ opt.label }}
              </label>
            </div>
          </div>

          <!-- Origen -->
          <div class="glv-filter-section">
            <label class="glv-filter-label">Origen</label>
            <div class="glv-radio-group">
              <label
                class="glv-radio"
                v-for="opt in originOptions"
                :key="opt.value"
              >
                <input type="radio" v-model="draft.origin" :value="opt.value" />
                {{ opt.label }}
              </label>
            </div>
          </div>
        </div>

        <!-- Footer sticky -->
        <div class="glv-filter-panel__footer">
          <button class="glv-filter-clear" @click="clearFilters">
            Limpiar filtros
          </button>
          <button class="glv-filter-apply" @click="applyFilters">Buscar</button>
        </div>
      </div>
    </transition>

    <!-- Modal de creación (reutiliza el componente existente) -->
    <ExpenseFormModal
      :show="showForm"
      @close="showForm = false"
      @saved="onExpenseSaved"
    />

    <!-- Modal de importación SRI -->
    <SriImportModal
      :visible="sriModalVisible"
      @close="sriModalVisible = false"
      @imported="onSriImported"
      @credentials-saved="onSriSaved"
    />
  </section>
</template>

<script>
import Api from "@/config/Api";
import ExpenseFormModal from "./components/ExpenseFormModal";
import SriImportModal from "./components/SriImportModal";

const CATEGORY_LABELS = {
  software: "Software",
  accounting: "Contabilidad",
  materials: "Materiales",
  subcontract: "Subcontrato",
  hosting: "Hosting",
  travel: "Viáticos",
  other: "Otros"
};

const DEFAULT_FILTERS = () => ({
  vendor: "",
  month: "",
  date_from: "",
  date_to: "",
  order: "",
  expense_type: "",
  categories: [],
  has_project: "all",
  origin: ""
});

export default {
  name: "GastosListView",
  components: { ExpenseFormModal, SriImportModal },

  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__clickOutside__ = e => {
          if (!el.contains(e.target)) binding.value(e);
        };
        document.addEventListener("mousedown", el.__clickOutside__);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el.__clickOutside__);
      }
    }
  },

  data() {
    return {
      loading: true,
      expenses: [],
      totals: {
        grand_total: 0,
        direct_total: 0,
        overhead_total: 0,
        sri_count: 0,
        manual_count: 0
      },
      filterOpen: false,
      showForm: false,
      sriModalVisible: false,
      filters: DEFAULT_FILTERS(),
      draft: DEFAULT_FILTERS(),

      // Período de navegación
      periodMode: "month", // 'month' | 'week'
      periodDate: new Date(), // fecha de referencia para el navegador

      // Estado credenciales SRI guardadas
      hasCredentials: false,
      sriSyncing: false,
      sriLastSync: null,

      typeOptions: [
        { value: "", label: "Todos" },
        { value: "direct", label: "Costo directo" },
        { value: "overhead", label: "Gasto operativo" }
      ],
      categoryOptions: Object.entries(
        CATEGORY_LABELS
      ).map(([value, label]) => ({ value, label })),
      projectOptions: [
        { value: "all", label: "Todos" },
        { value: "yes", label: "Con proyecto" },
        { value: "no", label: "Sin proyecto" }
      ],
      originOptions: [
        { value: "", label: "Todos" },
        { value: "sri", label: "Importadas del SRI" },
        { value: "manual", label: "Ingresadas manualmente" }
      ]
    };
  },

  computed: {
    businessId() {
      return this.$store.getters.getUser?.business_id;
    },

    activeFilterCount() {
      let n = 0;
      if (this.filters.vendor) n++;
      if (this.filters.month) n++;
      if (this.filters.expense_type) n++;
      if (this.filters.categories.length) n += this.filters.categories.length;
      if (this.filters.has_project && this.filters.has_project !== "all") n++;
      if (this.filters.origin) n++;
      return n;
    },

    periodLabel() {
      const d = this.periodDate;
      const MONTHS = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre"
      ];
      if (this.periodMode === "month") {
        return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      }
      // Semana: calcular lunes y domingo de la semana de `d`
      const day = d.getDay(); // 0=dom
      const diff = day === 0 ? -6 : 1 - day;
      const mon = new Date(d);
      mon.setDate(d.getDate() + diff);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      const fmt = dt =>
        `${dt.getDate()} ${MONTHS[dt.getMonth()].substring(0, 3)}`;
      return `${fmt(mon)} — ${fmt(sun)} ${sun.getFullYear()}`;
    },

    groupedByMonth() {
      const groups = {};
      this.expenses.forEach(e => {
        const d = new Date(e.expense_date + "T00:00:00");
        const months = [
          "Enero",
          "Febrero",
          "Marzo",
          "Abril",
          "Mayo",
          "Junio",
          "Julio",
          "Agosto",
          "Septiembre",
          "Octubre",
          "Noviembre",
          "Diciembre"
        ];
        const key = `${months[d.getMonth()]} ${d.getFullYear()}`;
        if (!groups[key]) groups[key] = { items: [], total: 0 };
        groups[key].items.push(e);
        groups[key].total += parseFloat(e.amount);
      });
      // Ordenar grupos cronológicamente desc
      return Object.fromEntries(
        Object.entries(groups).sort((a, b) => {
          const dateA = new Date(a[1].items[0].expense_date);
          const dateB = new Date(b[1].items[0].expense_date);
          return dateB - dateA;
        })
      );
    }
  },

  mounted() {
    this.setPeriodFilters();
    this.loadSriCredentials();
  },

  methods: {
    async loadExpenses() {
      this.loading = true;
      try {
        const params = { business_id: this.businessId };
        if (this.filters.vendor) params.vendor = this.filters.vendor;
        if (this.filters.month) params.month = this.filters.month;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        if (this.filters.expense_type)
          params.expense_type = this.filters.expense_type;
        if (this.filters.categories.length)
          params.categories = this.filters.categories.join(",");
        if (this.filters.has_project && this.filters.has_project !== "all")
          params.has_project = this.filters.has_project;
        if (this.filters.origin) params.origin = this.filters.origin;
        if (this.filters.order) params.order = this.filters.order;

        const res = await Api().get("/expenses", { params });
        if (res?.data?.success) {
          this.expenses = res.data.data;
          this.totals = res.data.totals;
        }
      } catch (e) {
        // silent
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.filters = { ...this.draft };
      this.filterOpen = false;
      this.loadExpenses();
    },

    clearFilters() {
      this.draft = DEFAULT_FILTERS();
      this.filters = DEFAULT_FILTERS();
      this.loadExpenses();
    },

    removeFilter(key) {
      this.filters[key] = DEFAULT_FILTERS()[key];
      this.draft[key] = DEFAULT_FILTERS()[key];
      this.loadExpenses();
    },

    removeCategoryFilter(cat) {
      this.filters.categories = this.filters.categories.filter(c => c !== cat);
      this.draft.categories = this.filters.categories.slice();
      this.loadExpenses();
    },

    closeFilter(e) {
      // Solo cierra si el clic fue fuera del panel Y fuera del botón de filtro
      const btn = this.$el.querySelector(
        ".glv-icon-btn.has-filters, .glv-icon-btn"
      );
      if (btn && btn.contains(e.target)) return;
      this.filterOpen = false;
    },

    openForm() {
      this.showForm = true;
    },

    onExpenseSaved() {
      this.showForm = false;
      this.loadExpenses();
    },

    onSriImported(count) {
      if (count > 0) {
        this.loadExpenses();
        this.$notify({
          type: "success",
          title: "SRI",
          message: `${count} factura(s) importada(s) correctamente.`
        });
      }
    },

    exportExcel() {
      const params = new URLSearchParams({
        business_id: this.businessId,
        ...this.filters
      }).toString();
      window.open(
        `${process.env.VUE_APP_API_URL}/api/expenses/export-excel?${params}`,
        "_blank"
      );
    },

    exportPdf() {
      const params = new URLSearchParams({
        business_id: this.businessId,
        ...this.filters
      }).toString();
      window.open(
        `${process.env.VUE_APP_API_URL}/api/expenses/export?${params}`,
        "_blank"
      );
    },

    categoryLabel(key) {
      return CATEGORY_LABELS[key] || key || "Sin categoría";
    },

    initials(name) {
      if (!name) return "??";
      const words = name.trim().split(" ");
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },

    avatarClass(expense) {
      const map = {
        software: "avatar--purple",
        accounting: "avatar--blue",
        materials: "avatar--orange",
        subcontract: "avatar--teal",
        hosting: "avatar--indigo",
        travel: "avatar--yellow",
        other: "avatar--grey"
      };
      return map[expense.category] || "avatar--grey";
    },

    formatAmount(val) {
      if (val === null || val === undefined) return "0.00";
      return parseFloat(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    },

    // ── Navegación de período ──────────────────────────────────────
    setPeriodFilters() {
      const d = this.periodDate;
      if (this.periodMode === "month") {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        this.filters.month = `${y}-${m}`;
        this.filters.date_from = "";
        this.filters.date_to = "";
      } else {
        const day = d.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        const mon = new Date(d);
        mon.setDate(d.getDate() + diff);
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        const fmt = dt =>
          `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(dt.getDate()).padStart(2, "0")}`;
        this.filters.month = "";
        this.filters.date_from = fmt(mon);
        this.filters.date_to = fmt(sun);
      }
      this.draft = { ...this.filters };
      this.loadExpenses();
    },

    prevPeriod() {
      const d = new Date(this.periodDate);
      if (this.periodMode === "month") d.setMonth(d.getMonth() - 1);
      else d.setDate(d.getDate() - 7);
      this.periodDate = d;
      this.setPeriodFilters();
    },

    nextPeriod() {
      const d = new Date(this.periodDate);
      if (this.periodMode === "month") d.setMonth(d.getMonth() + 1);
      else d.setDate(d.getDate() + 7);
      this.periodDate = d;
      this.setPeriodFilters();
    },

    goToToday() {
      this.periodDate = new Date();
      this.setPeriodFilters();
    },

    setPeriodMode(mode) {
      this.periodMode = mode;
      this.periodDate = new Date();
      this.setPeriodFilters();
    },

    // ── Credenciales y sync SRI ────────────────────────────────────
    async loadSriCredentials() {
      try {
        const res = await Api().get("/sri/credentials", {
          params: { business_id: this.businessId }
        });
        if (res?.data?.has_credentials) {
          this.hasCredentials = true;
          this.sriLastSync = res.data.last_sync_at;
        }
      } catch (e) {
        // silencioso — no es crítico
      }
    },

    async forceSyncSRI() {
      this.sriSyncing = true;
      try {
        const res = await Api().post("/sri/sync/force", {
          business_id: this.businessId
        });
        if (res?.data?.success) {
          const imp = res.data.imported || 0;
          this.$notify({
            type: "success",
            title: "Sync SRI",
            message:
              imp > 0
                ? `${imp} factura(s) importada(s) correctamente.`
                : "Ya estás al día. Sin facturas nuevas."
          });
          this.sriLastSync = res.data.synced_at;
          if (imp > 0) this.loadExpenses();
        } else {
          this.$notify({
            type: "error",
            title: "Sync SRI",
            message: res?.data?.message || "No se pudo sincronizar."
          });
        }
      } catch (e) {
        const msg = e?.response?.data?.message;
        if (msg?.includes("credenciales SRI")) {
          // No hay credenciales guardadas — abrir modal de importación manual
          this.sriModalVisible = true;
        } else {
          this.$notify({
            type: "error",
            title: "Sync SRI",
            message: msg || "Error de conexión."
          });
        }
      } finally {
        this.sriSyncing = false;
      }
    },

    onSriSaved() {
      // Se llama desde SriImportModal cuando el usuario guarda credenciales
      this.hasCredentials = true;
    }
  }
};
</script>

<style lang="scss">
// el-date-picker dentro del panel de filtros (no scoped — penetra el componente)
.glv-filter-panel .el-date-editor {
  width: 100% !important;

  .el-input__inner {
    border: none !important;
    border-bottom: 1.5px solid #eef0f8 !important;
    border-radius: 0 !important;
    font-size: 13px !important;
    color: #2d2b55 !important;
    background: transparent !important;
    height: 34px !important;
    line-height: 34px !important;
    padding-left: 0 !important;
    padding-right: 28px !important;
    box-shadow: none !important;
    &:focus {
      border-bottom-color: #3ecfb4 !important;
    }
  }

  .el-input__prefix {
    left: auto !important;
    right: 0 !important;
    color: #c0c4cc;
    font-size: 14px;
  }
  .el-input__suffix {
    color: #c0c4cc;
    right: 0 !important;
  }
}
</style>

<style lang="scss" scoped>
$teal: #3ecfb4;
$dark: #2d2b55;
$grey: #888;
$border: #e8e7e3;

// ── Layout ──────────────────────────────────────────────
.gastos-list-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  position: relative;
}

// ── Topbar ──────────────────────────────────────────────
.glv-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px 10px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__right {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $dark;
  }
}

.glv-btn-primary-action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 16px;
  border-radius: 8px;
  border: none;
  background: $teal;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  i {
    font-size: 13px;
  }
  &:hover {
    opacity: 0.85;
  }
}

// ── Export buttons (mismo patrón que ExportButtons.vue) ──
.export-btn-excel {
  color: #1d9e75;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.export-btn-label {
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}
.glv-btn-pdf {
  color: #534ab7;
  border: 1.5px solid #e4eaf6 !important;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  &:hover {
    border-color: #534ab7 !important;
    background: rgba(83, 74, 183, 0.04);
  }
}

.glv-btn-sri {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid #1d9e75;
  background: #e8f8f4;
  color: #1d9e75;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  i {
    font-size: 13px;
  }
  &:hover {
    background: #1d9e75;
    color: #fff;
  }
}

// Botón Sync SRI (diferente al de importación manual)
.glv-btn-sri-sync {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid $dark;
  background: transparent;
  color: $dark;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  i {
    font-size: 13px;
  }
  &:hover:not(:disabled) {
    background: $dark;
    color: #fff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &.is-syncing {
    border-color: $teal;
    color: $teal;
  }
}

// ── Barra de período ──────────────────────────────────────
.glv-period-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 18px;
  background: #fafbfd;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}

.glv-period-nav {
  display: flex;
  align-items: center;
  gap: 0;

  &__btn {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid $border;
    background: #fff;
    color: $dark;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    &:hover {
      background: $dark;
      color: #fff;
      border-color: $dark;
    }
  }

  &__label {
    min-width: 160px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: $dark;
    border: none;
    background: none;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 6px;
    transition: background 0.15s;
    &:hover {
      background: #f0f2f7;
    }
  }
}

.glv-period-toggle {
  display: flex;
  border: 1px solid $border;
  border-radius: 8px;
  overflow: hidden;

  &__btn {
    padding: 5px 14px;
    font-size: 12px;
    font-weight: 500;
    color: $grey;
    background: #fff;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
    & + & {
      border-left: 1px solid $border;
    }
    &.active {
      background: $dark;
      color: #fff;
    }
    &:not(.active):hover {
      background: #f0f2f7;
    }
  }
}

.glv-icon-btn {
  position: relative;
  background: none;
  border: none;
  color: $grey;
  font-size: 17px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
  &:hover {
    color: $dark;
    background: #f0f2f7;
  }
  &.has-filters {
    color: $teal;
  }
}

.glv-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #e24b4a;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
}

// ── Summary bar ─────────────────────────────────────────
.glv-summary {
  display: flex;
  align-items: center;
  padding: 10px 18px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
  gap: 4px;
  flex-wrap: wrap;

  &__item {
    display: flex;
    flex-direction: column;
    padding: 0 12px;
  }
  &__label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $grey;
    font-weight: 500;
  }
  &__value {
    font-size: 13px;
    font-weight: 600;
    color: $dark;
  }
  &__sep {
    width: 1px;
    height: 28px;
    background: $border;
    flex-shrink: 0;
  }
}

// ── Chips de filtros activos ─────────────────────────────
.glv-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 18px;
  background: #fff;
  border-bottom: 1px solid $border;
  flex-shrink: 0;
}

.glv-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef9f7;
  color: #1d9e75;
  border: 1px solid #c6ede6;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 12px;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    font-size: 10px;
    padding: 0;
    line-height: 1;
    &:hover {
      opacity: 0.7;
    }
  }
}

// ── Body (listado + panel filtro) ────────────────────────
.glv-body {
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  display: flex;
}

.glv-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

// ── Skeleton ─────────────────────────────────────────────
.glv-skeleton {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite;

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #e8eaf0;
    flex-shrink: 0;
  }
  &__lines {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__line {
    height: 10px;
    border-radius: 4px;
    background: #e8eaf0;
    &--lg {
      width: 60%;
    }
    &--sm {
      width: 40%;
    }
  }
  &__amount {
    width: 64px;
    height: 14px;
    border-radius: 4px;
    background: #e8eaf0;
    flex-shrink: 0;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

// ── Empty state ──────────────────────────────────────────
.glv-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  text-align: center;

  &__icon {
    font-size: 42px;
    color: #c0c4cc;
    margin-bottom: 12px;
  }
  &__text {
    font-size: 14px;
    color: $grey;
    margin-bottom: 16px;
  }
}

.glv-btn-primary {
  background: $teal;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.85;
  }
}

// ── Month group ──────────────────────────────────────────
.glv-month-group {
  margin-bottom: 20px;
}

.glv-month-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 0 4px;

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: $grey;
    font-weight: 600;
  }
  &__total {
    font-size: 11px;
    font-weight: 600;
    color: $dark;
  }
}

// ── Card ─────────────────────────────────────────────────
.glv-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border-radius: 10px;
  border: 0.5px solid $border;
  padding: 12px 14px;
  margin-bottom: 7px;
  transition: box-shadow 0.15s, transform 0.1s;
  cursor: default;

  &:hover {
    box-shadow: 0 2px 8px rgba(45, 43, 85, 0.07);
    transform: translateY(-1px);
  }

  &--no-category {
    border-color: #f7c1c1;
  }
}

// Avatars
.glv-card__avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;

  &.avatar--teal {
    background: #e8f8f4;
    color: $teal;
  }
  &.avatar--purple {
    background: #f0eeff;
    color: #7c5cbf;
  }
  &.avatar--blue {
    background: #e8f0ff;
    color: #3b6fd4;
  }
  &.avatar--orange {
    background: #fff3e8;
    color: #e07c2a;
  }
  &.avatar--indigo {
    background: #eef0ff;
    color: #5c6bc0;
  }
  &.avatar--yellow {
    background: #fffbe8;
    color: #c9a400;
  }
  &.avatar--grey {
    background: #f1efe8;
    color: $grey;
  }
}

// Card center
.glv-card__body {
  flex: 1;
  min-width: 0;
}
.glv-card__name {
  font-size: 13px;
  font-weight: 500;
  color: $dark;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}
.glv-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 3px;
}
.glv-card__desc {
  font-size: 11px;
  color: $grey;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.glv-card__no-cat {
  font-size: 11px;
  color: #e24b4a;
  font-weight: 500;
}

// Badge origen SRI
.glv-badge-origin {
  font-size: 10px;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 7px;
  &--sri {
    background: #e8f8f4;
    color: #1d9e75;
  }
}

// Chip SRI
.glv-chip--sri {
  background: #e8f8f4;
  color: #1d9e75;
  border-color: #c6ede6;
}

// Summary value teal
.glv-summary__value--teal {
  color: #1d9e75;
}

// Badges categoría
.glv-badge-cat {
  font-size: 10px;
  font-weight: 500;
  border-radius: 6px;
  padding: 2px 7px;
  &--software {
    background: #f0eeff;
    color: #7c5cbf;
  }
  &--accounting {
    background: #e8f0ff;
    color: #3b6fd4;
  }
  &--materials {
    background: #fff3e8;
    color: #e07c2a;
  }
  &--subcontract {
    background: #ffeef0;
    color: #c0445a;
  }
  &--hosting {
    background: #eef0ff;
    color: #5c6bc0;
  }
  &--travel {
    background: #fffbe8;
    color: #c9a400;
  }
  &--other {
    background: #f1efe8;
    color: $grey;
  }
}

.glv-badge-type {
  font-size: 10px;
  font-weight: 500;
  border-radius: 6px;
  padding: 2px 7px;
  &--direct {
    background: #e8f8f4;
    color: #1d9e75;
  }
  &--overhead {
    background: #f1efe8;
    color: $grey;
  }
}

.glv-badge-project {
  font-size: 10px;
  color: $dark;
  background: #f0f2f7;
  border-radius: 6px;
  padding: 2px 7px;
  display: flex;
  align-items: center;
  gap: 3px;
}

// Card right
.glv-card__right {
  flex-shrink: 0;
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}
.glv-card__amount {
  font-size: 14px;
  font-weight: 500;
  color: $dark;
}
.glv-card__date {
  font-size: 11px;
  color: $grey;
  white-space: nowrap;
}

// ── Overlay ───────────────────────────────────────────────
.glv-filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(45, 43, 85, 0.25);
  z-index: 1040;
}

.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity 0.22s ease;
}
.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}

// ── Panel de filtros ─────────────────────────────────────
.glv-filter-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 320px;
  background: #fff;
  border-left: 1px solid $border;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  box-shadow: -6px 0 24px rgba(45, 43, 85, 0.14);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid $border;
    font-size: 14px;
    font-weight: 600;
    color: $dark;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-top: 1px solid $border;
    flex-shrink: 0;
    background: #fff;
  }
}

.glv-filter-section {
  padding: 10px 0;
}

.glv-filter-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #909399;
  margin-bottom: 8px;
  text-transform: none;
  letter-spacing: normal;

  &__info {
    color: $teal;
    font-size: 13px;
  }
}

.glv-filter-input-wrap {
  position: relative;
}

.glv-filter-input {
  width: 100%;
  padding: 8px 28px 8px 0;
  border: none;
  border-bottom: 1.5px solid #eef0f8;
  border-radius: 0;
  font-size: 13px;
  color: $dark;
  background: transparent;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus {
    border-bottom-color: $teal;
  }
  &::placeholder {
    color: #c0c4cc;
    font-weight: 400;
  }
}

.glv-filter-input__icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: $grey;
  font-size: 14px;
  pointer-events: none;
}

.glv-order-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}
.glv-order-btn {
  padding: 8px 6px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1.5px solid #eef0f8;
  background: #fff;
  color: #909399;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
  &:hover {
    border-color: $teal;
    color: $dark;
    background: rgba($teal, 0.04);
  }
  &.active {
    background: $dark;
    border-color: $dark;
    color: #fff;
  }
}

.glv-radio-group,
.glv-check-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.glv-radio,
.glv-check {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;

  input[type="radio"],
  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 1.5px solid #d1d5db;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    flex-shrink: 0;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    position: relative;

    &:checked {
      border-color: $teal;
      background: $teal;
      &::after {
        content: "";
        position: absolute;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #fff;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }
  }

  input[type="checkbox"] {
    border-radius: 4px;
    &:checked {
      &::after {
        content: "✓";
        font-size: 10px;
        font-weight: 700;
        color: #fff;
        border-radius: 0;
        background: transparent;
        width: auto;
        height: auto;
      }
    }
  }
}

.glv-filter-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: $grey;
  font-size: 13px;
  &:hover {
    color: $dark;
  }
}

.glv-filter-apply {
  background: $teal;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.85;
  }
}

// ── Transición slide del panel ───────────────────────────
.slide-filter-enter-active,
.slide-filter-leave-active {
  transition: transform 0.22s ease;
}
.slide-filter-enter,
.slide-filter-leave-to {
  transform: translateX(100%);
}
</style>
